import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { X } from 'lucide-react';
import { DOCUMENT_VERSIONS_QUERY } from '../graphql/queries';
import { RESTORE_DOCUMENT_VERSION_MUTATION } from '../graphql/mutations';
import { FOCUS_RING_CLASSES, trapTabKey } from '../lib/focusTrap';
import { useScrollLock } from '../hooks/useScrollLock';

interface DocumentVersionData {
  version: number;
  author: string;
  createdAt: string;
}

interface DocumentVersionsData {
  documentVersions: DocumentVersionData[];
}

interface DocumentVersionsDialogProps {
  path: string;
  onClose: () => void;
  /** Called after a successful restore with the lowercased, in-sentence time phrase. */
  onRestored: (restoredToTime: string) => void;
}

const AUTHOR_LABELS: Record<string, string> = {
  user: 'You',
  agent: 'Me',
  system: 'Automatic',
};

/** "Today at 3:12 PM", "Yesterday at 9:04 AM" or "Jan 5, 2026 at 3:12 PM". */
function formatVersionTime(iso: string): string {
  const d = parseISO(iso);
  if (isToday(d)) return 'Today at ' + format(d, 'h:mm a');
  if (isYesterday(d)) return 'Yesterday at ' + format(d, 'h:mm a');
  return format(d, "MMM d, yyyy 'at' h:mm a");
}

/** Same as formatVersionTime, but for use mid-sentence: lowercased and "on " before full dates. */
function formatVersionTimeInSentence(iso: string): string {
  const d = parseISO(iso);
  if (isToday(d)) return 'today at ' + format(d, 'h:mm a');
  if (isYesterday(d)) return 'yesterday at ' + format(d, 'h:mm a');
  return 'on ' + format(d, "MMM d, yyyy 'at' h:mm a");
}

function isNotFoundError(error: ApolloError): boolean {
  const code = error.graphQLErrors[0]?.extensions?.code;
  if (code === 'NOT_FOUND') return true;
  return /not[ _-]?found/i.test(error.message);
}

/** Earlier versions of a document: browse them and restore one. */
export function DocumentVersionsDialog({ path, onClose, onRestored }: DocumentVersionsDialogProps) {
  const [view, setView] = useState<'list' | 'confirm'>('list');
  const [selected, setSelected] = useState<DocumentVersionData | null>(null);
  const [confirmError, setConfirmError] = useState<'not_found' | 'generic' | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const confirmHeadingRef = useRef<HTMLHeadingElement>(null);

  useScrollLock(true);

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery<DocumentVersionsData>(DOCUMENT_VERSIONS_QUERY, {
    variables: { path },
    fetchPolicy: 'network-only',
  });

  // Refetch the page's document (and the tree) in the background; the dialog
  // closes on restore, so we don't await — and never refetch this dialog's own
  // (soon-to-unmount) versions query.
  const [restoreVersion, { loading: restoring }] = useMutation(RESTORE_DOCUMENT_VERSION_MUTATION, {
    refetchQueries: ['DocumentContent', 'Documents'],
  });

  useEffect(() => {
    if (view === 'confirm') {
      confirmHeadingRef.current?.focus();
    } else {
      panelRef.current?.focus();
    }
  }, [view]);

  const versions = (data?.documentVersions ?? []).slice(0, 10);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(e, panelRef.current);
  };

  const openConfirm = (version: DocumentVersionData) => {
    setSelected(version);
    setConfirmError(null);
    setView('confirm');
  };

  const backToList = () => {
    setSelected(null);
    setConfirmError(null);
    setView('list');
  };

  const handleBackToRefetchedList = async () => {
    try {
      await refetch();
    } catch {
      // The list view surfaces its own query error state.
    }
    backToList();
  };

  const handleRestore = async () => {
    if (!selected) return;
    setConfirmError(null);
    try {
      await restoreVersion({ variables: { path, version: selected.version } });
      onRestored(formatVersionTimeInSentence(selected.createdAt));
    } catch (err) {
      const apolloError = err as ApolloError;
      setConfirmError(isNotFoundError(apolloError) ? 'not_found' : 'generic');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="glass-panel bg-[var(--surface)] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-warm animate-fade-in-up safe-bottom"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-versions-title"
        tabIndex={-1}
      >
        <div
          aria-hidden
          className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-[var(--border)] sm:hidden"
        />
        <div className="flex items-center justify-between mb-4">
          <h2
            id="document-versions-title"
            className="text-xl font-semibold text-[var(--text-primary)]"
          >
            Earlier versions
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 hover:bg-[color-mix(in_oklab,var(--text-primary)_7%,transparent)] rounded-full transition-colors ${FOCUS_RING_CLASSES}`}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-[var(--text-tertiary)]" aria-hidden />
          </button>
        </div>

        <div className="max-h-[60dvh] overflow-y-auto overscroll-contain">
          {view === 'list' && (
            <>
              {loading ? (
                <div className="py-8 text-center" role="status">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)] mx-auto" />
                  <span className="sr-only">Loading earlier versions…</span>
                </div>
              ) : queryError ? (
                <p
                  role="alert"
                  className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl text-sm"
                >
                  Couldn't load earlier versions. Close this and try again.
                </p>
              ) : versions.length < 2 ? (
                <p className="text-[var(--text-secondary)] text-center py-6">
                  No earlier versions yet. When this document changes, its earlier versions will
                  show up here.
                </p>
              ) : (
                <>
                  <ul className="divide-y divide-[var(--border)]">
                    {versions.map((version, index) => {
                      const formattedTime = formatVersionTime(version.createdAt);
                      return (
                        <li key={version.version} className="flex items-center gap-3 py-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[var(--text-primary)]">
                              {AUTHOR_LABELS[version.author] ?? 'Automatic'}
                              {index === 0 && <span className="sr-only"> — current version</span>}
                            </p>
                            <p className="text-xs text-[var(--text-tertiary)]">{formattedTime}</p>
                          </div>
                          {index === 0 ? (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[color-mix(in_oklab,var(--marine-400)_15%,transparent)] text-[var(--accent-ink)] shrink-0">
                              Current
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openConfirm(version)}
                              className="btn btn-ghost text-sm shrink-0"
                              aria-label={`Restore the version from ${formattedTime}`}
                            >
                              Restore
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="help mt-2">I keep the last 10 versions of this document.</p>
                </>
              )}
            </>
          )}

          {view === 'confirm' && selected && (
            <>
              <h3
                ref={confirmHeadingRef}
                tabIndex={-1}
                className="text-base font-semibold text-[var(--text-primary)]"
              >
                Restore this version?
              </h3>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                The document will go back to how it was{' '}
                {formatVersionTimeInSentence(selected.createdAt)}. Nothing is lost — the version you
                have now is kept, so you can undo this too.
              </p>

              {confirmError && (
                <div
                  role="alert"
                  className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl text-sm mt-4"
                >
                  {confirmError === 'not_found'
                    ? "That version is no longer available — I keep only the last 10. The document hasn't changed."
                    : "Something went wrong — the document hasn't changed. Try again."}
                </div>
              )}

              <div className="mt-5 flex gap-2 justify-end">
                {confirmError === 'not_found' ? (
                  <button
                    type="button"
                    onClick={handleBackToRefetchedList}
                    className="btn btn-ghost"
                  >
                    Back to versions
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={backToList}
                      className="btn btn-ghost"
                      disabled={restoring}
                    >
                      Keep it as it is
                    </button>
                    <button
                      type="button"
                      onClick={handleRestore}
                      className="btn btn-primary"
                      disabled={restoring}
                    >
                      {restoring ? 'Restoring…' : 'Restore'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
